// frontend/src/components/layout/TopMenu.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';

import {
  ICON, MenuIcon, MagnifyingGlass, Bell, Question, Sun, Moon,
} from '../../icons';

import UserDropdown from './UserDropdown';
import NotificationDropdown from './NotificationDropdown';
import HelpMenu from './HelpMenu';
import ContactSupportModal from '../common/ContactSupportModal';
import CommandPalette from '../commandpalette/CommandPalette';
import KeyboardShortcutsHelp from '../commandpalette/KeyboardShortcutsHelp';
import MobileChrome from './mobile/MobileChrome';

import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import useCommandPalette from '../../hooks/useCommandPalette';
import useThemeToggle from '../../hooks/useThemeToggle';
import useRootAttribute from '../../hooks/useRootAttribute';
import { avatarColor, initials, sessionIdentity } from '../../utils/avatar';
import '../../styles/avatar-chip.css';
import './TopMenu.css';

// The bar's logo, desktop or mobile size. Exported for EntryBackdrop, which
// draws a still copy of the bars behind the entry screens; the phone's live
// bar shows the store instead (mobile/StoreSwitcher.jsx).
export function TopMenuLogo({ mobile = false }) {
  return (
    <img
      src="/images/manexys-logo.png"
      alt="Manexys"
      className={mobile ? 'top-menu-logo-mobile' : 'top-menu-logo'}
    />
  );
}

const TopMenu = () => {
  const { t } = useTranslation();
  const { user, updateSidebar, selectedStore } = useAuth();
  const theme = useThemeToggle();
  // Read off <html>, like the theme: userContext.user.sidebar_collapsed lags
  // one PATCH, so a fast second click would send the value the first click
  // already sent.
  const sidebarCollapsed = useRootAttribute('data-sidebar') === 'collapsed';
  const { hasNotifications } = useNotifications();
  const commandPalette = useCommandPalette();
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const [helpDropdownOpen, setHelpDropdownOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const userDropdownRef = useRef(null);
  const notifDropdownRef = useRef(null);
  const helpDropdownRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) setUserDropdownOpen(false);
      if (notifDropdownRef.current && !notifDropdownRef.current.contains(e.target)) setNotifDropdownOpen(false);
      if (helpDropdownRef.current && !helpDropdownRef.current.contains(e.target)) setHelpDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setShortcutsOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // The trigger chip shows the current store, or the user when no store is
  // selected — resolved once so the label, the tooltip and the initials can
  // never disagree about which of the two it is showing.
  const { label: chipLabel, seed: chipSeed } = sessionIdentity(user, selectedStore);

  return (
    <>
      {/* Desktop Top Bar — hamburger + logo left, utilities right */}
      <nav className="top-menu">
        <div className="top-menu-left">
          <button
            className="top-menu-sidebar-toggle"
            onClick={() => updateSidebar(!sidebarCollapsed)}
            aria-label={t(sidebarCollapsed ? 'topMenu.openMenu' : 'topMenu.closeMenu')}
            aria-expanded={!sidebarCollapsed}
          >
            <MenuIcon size={ICON.lg} />
          </button>
          <div className="top-menu-logo-block">
            <TopMenuLogo />
          </div>
        </div>
        <div className="top-menu-right">
          <Tooltip title={t('commandPalette.title')}>
            <button className="top-menu-icon-btn" onClick={commandPalette.open} aria-label={t('commandPalette.title')}>
              <MagnifyingGlass size={ICON.lg} />
            </button>
          </Tooltip>

          <Tooltip title={theme.label}>
            <button
              className="top-menu-icon-btn theme-toggle-btn"
              onClick={theme.toggle}
              aria-label={t('topMenu.switchTheme')}
            >
              <span className="theme-icon theme-icon-sun"><Sun size={ICON.lg} /></span>
              <span className="theme-icon theme-icon-moon"><Moon size={ICON.lg} /></span>
            </button>
          </Tooltip>

          <div className="top-menu-notification-container" ref={notifDropdownRef}>
            <Tooltip title={notifDropdownOpen ? '' : t('topMenu.notifications')}>
              <button
                className="top-menu-icon-btn"
                onClick={() => setNotifDropdownOpen(!notifDropdownOpen)}
                aria-label={t('topMenu.notifications')}
              >
                <Bell size={ICON.lg} />
                {hasNotifications && <span className="top-menu-notification-dot" />}
              </button>
            </Tooltip>
            <NotificationDropdown isOpen={notifDropdownOpen} onClose={() => setNotifDropdownOpen(false)} />
          </div>

          <div className="top-menu-notification-container" ref={helpDropdownRef}>
            <Tooltip title={helpDropdownOpen ? '' : t('topMenu.help')}>
              <button
                className="top-menu-icon-btn"
                onClick={() => setHelpDropdownOpen(!helpDropdownOpen)}
                aria-label={t('topMenu.help')}
              >
                <Question size={ICON.lg} />
              </button>
            </Tooltip>
            <HelpMenu
              isOpen={helpDropdownOpen}
              onClose={() => setHelpDropdownOpen(false)}
              onOpenContact={() => setContactOpen(true)}
              onOpenShortcuts={() => setShortcutsOpen(true)}
            />
          </div>

          <div className="top-menu-user-dropdown-container" ref={userDropdownRef}>
            {/* Single trigger chip — the current store, or the user when none
                is selected so the dropdown always has a trigger. The user's own
                chip lives inside the dropdown, by their name. */}
            <Tooltip title={userDropdownOpen ? '' : chipLabel}>
              <button
                className="top-menu-store-chip avatar-chip avatar-chip--sm"
                style={{ background: avatarColor(chipSeed) }}
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                aria-label={chipLabel}
              >
                {initials(chipLabel)}
              </button>
            </Tooltip>
            <UserDropdown isOpen={userDropdownOpen} onClose={() => setUserDropdownOpen(false)} />
          </div>
        </div>
      </nav>

      <MobileChrome onSearch={commandPalette.open} onOpenContact={() => setContactOpen(true)} />

      <CommandPalette {...commandPalette} />
      <KeyboardShortcutsHelp isOpen={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <ContactSupportModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </>
  );
};

export default TopMenu;
